import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, InfoIcon, MailIcon } from 'lucide-react';
import { ConsultantAvatar } from '../components/consultants/ConsultantAvatar';
import { ButtonLink } from '../components/ui/ButtonLink';
import { PageHero } from '../components/ui/PageHero';
import { consultants, searchApproach } from '../data/consultants';
import { usePageMeta } from '../hooks/usePageMeta';
import { buttonClasses } from '../utils/buttonStyles';
import { NotFound } from './NotFound';
export function ConsultantProfile() {
  const {
    slug
  } = useParams();
  const consultant = consultants.find(c => c.slug === slug);
  usePageMeta(consultant ? `${consultant.name}, ${consultant.title}` : 'Consultant not found', consultant ? `${consultant.name} is an Executive Search Consultant with Aurevane Search Partners. Contact ${consultant.firstName} directly by email.` : 'The consultant profile you are looking for could not be found.', consultant ? {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: consultant.name,
    jobTitle: consultant.title,
    worksFor: {
      '@type': 'Organization',
      name: 'Aurevane Search Partners'
    }
  } : undefined);
  if (!consultant) return <NotFound data-id="element-631" data-mp="12h602kbl2o3h" />;
  const others = consultants.filter(c => c.slug !== consultant.slug);
  const mailto = `mailto:${consultant.email}`;
  return <>
      <PageHero compact label={consultant.title} title={consultant.name} data-id="element-632" data-mp="thiv5ojnb05o" />

      <section className="bg-porcelain py-16 lg:py-24" data-id="element-633" data-mp="xjxc233r7pub">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-634" data-mp="xliaw4x3puxz">
          <aside className="lg:col-span-4" data-id="element-635" data-mp="jm8rkwdlmkps">
            <div className="rounded-2xl border border-plum/10 bg-white p-6 lg:sticky lg:top-28" data-id="element-636" data-mp="oiz81ktbfibq">
              <ConsultantAvatar consultant={consultant} data-id="element-637" data-mp="2dxxbze2lnfhk" />
              <h2 className="mt-6 text-2xl font-semibold text-aubergine" data-id="element-638" data-mp="2dz8i3kjiozbu">{consultant.name}</h2>
              <p className="mt-1 text-sm text-plum/65" data-id="element-639" data-mp="2iwqpv7lst8i9">{consultant.title}</p>
              <a href={mailto} className={buttonClasses('primary', 'md', 'mt-6 w-full')} data-id="element-640" data-mp="2hh4jax1gkiqt">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-641" data-mp="2cg8e8g5deqjt" />
                Email {consultant.firstName}
              </a>
              <Link to="/consultants" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-plum/70 hover:text-copper-deep" data-id="element-642" data-mp="27if9lrjpu4ln">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-643" data-mp="307dswrzkrcsj" />
                All consultants
              </Link>
            </div>
          </aside>

          <div className="space-y-14 lg:col-span-7 lg:col-start-6" data-id="element-644" data-mp="2v9uftvem5626">
            <section aria-labelledby="intro-title" data-id="element-645" data-mp="2qfq7p8tm2rzi">
              <h2 id="intro-title" className="text-2xl font-semibold text-aubergine" data-id="element-646" data-mp="2lhy95vygo7mc">
                Professional introduction
              </h2>
              <p className="mt-4 max-w-prose text-lg leading-relaxed text-plum/75" data-id="element-647" data-mp="3iy3xcl4sn7ni">{consultant.introduction}</p>
              {!consultant.biographyConfirmed && <p className="mt-4 flex gap-2 text-sm text-plum/60" data-id="element-648" data-mp="3mfrmg7yfrpsc">
                  <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper-deep" aria-hidden="true" data-id="element-649" data-mp="3mhm8jplg1ny0" />
                  Placeholder — an approved biography has not yet been provided.
                </p>}
            </section>

            <section aria-labelledby="expertise-title" className="border-t border-copper/30 pt-12" data-id="element-650" data-mp="49wtjyl46kc7">
              <h2 id="expertise-title" className="text-2xl font-semibold text-aubergine" data-id="element-651" data-mp="99j583lansiw">
                Areas of expertise
              </h2>
              {consultant.expertise.length ? <ul className="mt-5 flex flex-wrap gap-2" data-id="element-652" data-mp="i0jk33gq13x7">
                  {consultant.expertise.map(e => <li key={e} className="rounded-full border border-plum/15 bg-white px-3.5 py-1.5 text-sm text-plum/80" data-id="element-653" data-mp="mz3591nhqboi">
                      {e}
                    </li>)}
                </ul> : <p className="mt-4 leading-relaxed text-plum/65" data-id="element-654" data-mp="ww7sl4t2od6w">Confirmed areas of expertise will be listed here once provided.</p>}
            </section>

            <section aria-labelledby="approach-title" className="border-t border-copper/30 pt-12" data-id="element-655" data-mp="1opt95m15g9oe">
              <h2 id="approach-title" className="text-2xl font-semibold text-aubergine" data-id="element-656" data-mp="23e7afsix3q2u">
                Search approach
              </h2>
              <ul className="mt-5 space-y-4" data-id="element-657" data-mp="1yiryj64h0492">
                {searchApproach.map(a => <li key={a} className="flex gap-3 leading-relaxed text-plum/75" data-id="element-658" data-mp="1asxpkzn9uzu3">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-659" data-mp="15tl16agr5kpe" />
                    {a}
                  </li>)}
              </ul>
            </section>

            <section aria-label="Get in touch" className="grid gap-5 border-t border-copper/30 pt-12 sm:grid-cols-2" data-id="element-660" data-mp="1fomqb6b12mrz">
              <div className="flex flex-col rounded-2xl bg-limestone p-6" data-id="element-661" data-mp="2v8j543q1dhv8">
                <h2 className="font-display text-lg font-semibold text-aubergine" data-id="element-662" data-mp="2r4tihjsx9acu">For candidates</h2>
                <p className="mt-2 text-sm leading-relaxed text-plum/75" data-id="element-663" data-mp="2ynawa27tzbbm">
                  Introduce your background directly to {consultant.firstName} by email, or register your interest with the wider team.
                </p>
                <a href={mailto} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-copper-deep hover:underline" data-id="element-664" data-mp="2tpj6peuns1al">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-665" data-mp="3hg51i4fnjp2n" />
                  Email {consultant.firstName}
                </a>
                <ButtonLink to="/candidates#introduce" variant="ghost" arrow className="mt-auto pt-5" data-id="element-666" data-mp="3mg9hltcnehsc">
                  Introduce Yourself
                </ButtonLink>
              </div>
              <div className="on-dark flex flex-col rounded-2xl bg-aubergine p-6 text-porcelain" data-id="element-667" data-mp="3cmkc30s4k3f2">
                <h2 className="font-display text-lg font-semibold" data-id="element-668" data-mp="478yu2ivi6ff">For employers</h2>
                <p className="mt-2 text-sm leading-relaxed text-porcelain/75" data-id="element-669" data-mp="90218jqvd2oc">
                  Planning a leadership appointment? Share your requirements and {consultant.firstName} or a colleague will be in touch.
                </p>
                <ButtonLink to="/employers#inquiry" size="sm" className="mt-auto self-start" data-id="element-670" data-mp="ducyzsu1m98k">
                  Submit Hiring Inquiry
                </ButtonLink>
              </div>
            </section>
          </div>
        </div>
      </section>

      <section className="border-t border-plum/10 bg-porcelain py-16" data-id="element-671" data-mp="3w2u96tk258k4">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-672" data-mp="3rbc885i1b7z5">
          <h2 className="font-display text-lg font-semibold text-aubergine" data-id="element-673" data-mp="3r9reuj7ghv2v">Other consultants</h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-674" data-mp="3mc918fihuroj">
            {others.map(c => <li key={c.slug} data-id="element-675" data-mp="3ic8p51ivaskn">
                <Link to={`/consultants/${c.slug}`} className="group flex items-center gap-4 rounded-xl border border-plum/10 bg-white p-4 transition-colors duration-150 hover:border-copper/50" data-id="element-676" data-mp="3dbb6yoxuxyj5">
                  <ConsultantAvatar consultant={c} size="sm" data-id="element-677" data-mp="1nu5xl9c2fhnb" />
                  <span data-id="element-678" data-mp="1ntnqtirs1l15">
                    <span className="block font-display font-semibold text-aubergine group-hover:text-copper-deep" data-id="element-679" data-mp="1nwjpj9bg8dqn">{c.name}</span>
                    <span className="block text-xs text-plum/60" data-id="element-680" data-mp="1xrttw3pbkx7l">{c.title}</span>
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}